import Decimal from 'decimal.js';

import {assert} from 'Asserts';
import {DecimalNotation} from 'graphql-client/__generated/graphqlGlobalTypes';
import {assertNever} from 'util/Types';

// Prefer importing from this file instead of graphqlGlobalTypes for clearer dependencies
export {DecimalNotation};

export type PreciseDecimalConvertableValue = string | number | Decimal | PreciseDecimal;

/** RegEx for parsing decimal numbers that can be converted to a PreciseDecimal.
 *
 * Pulls apart a decimal string into named match groups.
 * Example: "-123.40e+2" -> {
 *   sign: "-"
 *   decimal: "123.40"
 *   wholeNumber: "123"
 *   fractionalPart: "40"
 *   scientificSuffix: "e+2"
 *   exponent: "+2"
 * }
 *
 * Keep in sync with packages/benchling-precise-decimal/src/benchling_precise_decimal/precise_decimal.py SANITIZABLE_DECIMAL_REGEX.
 */
export const SANITIZABLE_DECIMAL_REGEX =
  /^\s*(?<sign>[+-])?(?=\d|\.\d)(?<decimal>(?<wholeNumber>\d*)(?:\.(?<fractionalPart>\d*))?)(?<scientificSuffix>E(?<exponent>[+-]?\d+))?\s*$/i;

/**
 * Immutable decimal value that preserves exact decimal representation.
 *
 * Values are stored as standardized strings.
 * Use instead of float or Decimal when decimal precision or significant figures matter.
 * Keep in sync with packages/benchling-precise-decimal/src/benchling_precise_decimal/precise_decimal.py.
 */
export default class PreciseDecimal {
  readonly value: string;
  /** The current notation of the decimal value. Defaults to STANDARD. */
  readonly notation: DecimalNotation;
  /** The value as a decimal.js Decimal for performing arithmetic. */
  readonly decimal: Decimal;

  /** The unambiguous regex pattern that all PreciseDecimal strings must follow.
   *
   * Supports integers (-12300), standard notation decimals (12300.123000), and
   * scientific notation decimals (1.23000E+4).
   * Does not support leading zeros, leading +, trailing decimal points, or locale grouping.
   *
   * Keep in sync with packages/benchling-precise-decimal/src/benchling_precise_decimal/precise_decimal.py PRECISE_DECIMAL_REGEX.
   */
  static readonly PRECISE_DECIMAL_REGEX =
    /^(?<sign>-)?(?:(?<standardDecimal>(?:[1-9]\d*|0)(?:\.\d+)?)|(?<scientificCoefficient>[1-9](?:\.\d+)?)E(?<scientificExponent>(?:\+0)|(?:[+-][1-9]\d*)))$/;

  constructor(value: string | PreciseDecimal) {
    if (value instanceof PreciseDecimal) {
      this.value = value.value;
      this.notation = value.notation;
      this.decimal = value.decimal;
    } else {
      if (!PreciseDecimal.PRECISE_DECIMAL_REGEX.test(value)) {
        throw new TypeError(`Invalid PreciseDecimal string: ${value}`);
      }
      this.value = value;
      this.notation = value.includes('E') ? DecimalNotation.SCIENTIFIC : DecimalNotation.STANDARD;
      this.decimal = new Decimal(value);
    }
  }

  /** Converts a value into a PreciseDecimal.
   *
   * If the value cannot be converted, throws a TypeError.
   *
   * Numbers and Decimals are stringified for conversion according to their base JavaScript
   * implementation, which may not produce the desired notation or precision. To guarantee
   * specific behavior, convert the value to the expected string representation before
   * providing it as a parameter.
   */
  static of(value: PreciseDecimalConvertableValue): PreciseDecimal {
    const preciseDecimal = PreciseDecimal.ofOrUndefined(value);
    if (preciseDecimal == null) {
      throw new TypeError(`Invalid PreciseDecimal value: ${value}`);
    }
    return preciseDecimal;
  }

  /** Converts a value into a PreciseDecimal.
   *
   * Returns undefined if the value cannot be converted.
   */
  static ofOrUndefined(value: PreciseDecimalConvertableValue | undefined | null): PreciseDecimal | undefined {
    if (value == null) {
      return undefined;
    }
    const result = toPreciseDecimalValue(value);
    if ('error' in result) {
      return undefined;
    }
    return new PreciseDecimal(result.value);
  }

  /** Convert to a string (same as .value). */
  toString(): string {
    return this.value;
  }

  toNotation(notation: DecimalNotation): PreciseDecimal {
    if (this.notation === notation) {
      return this;
    }
    switch (notation) {
      case DecimalNotation.STANDARD:
        return this.toStandardNotation();
      case DecimalNotation.SCIENTIFIC:
        return this.toScientificNotation();
      default:
        assertNever(notation);
    }
    return this;
  }

  /** Converts value to standard notation, preserving digit precision. */
  toStandardNotation(): PreciseDecimal {
    if (this.notation === DecimalNotation.STANDARD) {
      return this;
    }
    return convertScientificPreciseDecimalStringToStandard(this);
  }

  /** Converts value to scientific notation, preserving digit precision. */
  toScientificNotation(): PreciseDecimal {
    if (this.notation === DecimalNotation.SCIENTIFIC) {
      return this;
    }
    const convertedScientificDecimal = sanitizeToPreciseDecimal(`${this.value}E+0`);
    if (convertedScientificDecimal === this.value) {
      return this;
    }
    return new PreciseDecimal(convertedScientificDecimal);
  }

  /** ⚠️ Caution! Convert to a JS binary float number.
   *
   * May lose precision for values with many digits, so only use when doing math with other floats. Prefer
   * using PreciseDecimal.decimal for math instead.
   */
  toFloat(): number {
    return this.decimal.toNumber();
  }

  /** Check if two PreciseDecimals are equal. */
  equals(other: PreciseDecimal): boolean {
    return this.value === other.value;
  }

  valueOf(): string {
    return this.value;
  }
}

/** Convert a value to a PreciseDecimal-compatible value string.
 * Or return an error string if the value cannot be converted.
 *
 * This is separate so that we can choose to not throw an error and return undefined instead.
 */
function toPreciseDecimalValue(value: PreciseDecimalConvertableValue): {value: string} | {error: string} {
  const CANNOT_CONVERT_ERROR = 'cannot be converted to a valid finite PreciseDecimal';
  let stringValue: string = '';
  let inputType: string = typeof value;
  if (typeof value === 'string') {
    stringValue = value;
    inputType = 'string';
  } else if (typeof value === 'number') {
    stringValue = String(value);
    inputType = 'number';
  } else if (value instanceof Decimal) {
    stringValue = value.toString();
    inputType = 'decimal';
  } else if (value instanceof PreciseDecimal) {
    stringValue = value.value;
    inputType = 'precise decimal';
  }
  if (!stringValue.match(SANITIZABLE_DECIMAL_REGEX)) {
    return {error: `${inputType} ${value} ${CANNOT_CONVERT_ERROR}`};
  }
  return {value: sanitizeToPreciseDecimal(stringValue)};
}

/** A function to convert a parsable decimal string into a PreciseDecimal string. */
function sanitizeToPreciseDecimal(value: string): string {
  const match = value.match(SANITIZABLE_DECIMAL_REGEX);
  assert(match != null, `String cannot be parsed into a PreciseDecimal`, {value}, undefined, {
    ErrorClass: TypeError,
  });
  const {sign, wholeNumber, fractionalPart, scientificSuffix, exponent} =
    (match.groups as Record<string, string | undefined>) ?? {};
  // Only a negative leading sign is kept
  const signString = sign === '-' ? '-' : '';
  // Remove leading zeros, except for one
  let wholeNumberString = stripLeadingZeros(wholeNumber ?? '');
  let fractionalPartString = fractionalPart ?? '';
  // If all digits are zeros, standardize to 0(.0...) to retain precision, but do not apply a sign
  if (wholeNumberString === '0' && fractionalPartString.length === countLeadingZeros(fractionalPartString)) {
    return fractionalPart ? `0.${fractionalPart}` : '0';
  }
  let decimalString = fractionalPartString
    ? `${wholeNumberString}.${fractionalPartString}`
    : wholeNumberString;
  if (scientificSuffix) {
    assert(exponent != null, 'Exponent should have been matched from input string', {value});
    // Keep track of how the exponent should be adjusted when putting the coefficient into the standard
    // format for scientific notation.
    let exponentAdjuster: number = 0;
    // If less than 1, adjust upward
    if (wholeNumberString === '0') {
      const leadingFractionalZeros = countLeadingZeros(fractionalPartString);
      exponentAdjuster -= leadingFractionalZeros + 1;
      wholeNumberString = fractionalPartString.slice(leadingFractionalZeros, leadingFractionalZeros + 1);
      fractionalPartString = fractionalPartString.slice(leadingFractionalZeros + 1);
      decimalString = fractionalPartString
        ? `${wholeNumberString}.${fractionalPartString}`
        : wholeNumberString;
    } else {
      // Otherwise, adjust downward if >= 10
      const singleDigit = wholeNumberString.slice(0, 1);
      const otherDigits = wholeNumberString.slice(1);
      exponentAdjuster += otherDigits.length;
      fractionalPartString = `${otherDigits}${fractionalPartString}`;
      decimalString = fractionalPartString ? `${singleDigit}.${fractionalPartString}` : singleDigit;
    }
    const exponentInt: number = parseInt(exponent, 10) + exponentAdjuster;
    assert(Number.isFinite(exponentInt), `Exponent is not a finite integer`, {exponentInt}, undefined, {
      ErrorClass: TypeError,
    });
    const exponentSignString = exponentInt >= 0 ? '+' : '';
    return `${signString}${decimalString}E${exponentSignString}${exponentInt}`;
  }
  return `${signString}${decimalString}`;
}

function stripLeadingZeros(wholeNumber: string): string {
  return wholeNumber.replace(/^0+/, '') || '0';
}

function countLeadingZeros(wholeNumber: string): number {
  return wholeNumber.match(/^0+/)?.[0]?.length ?? 0;
}

/** Expands a scientific PreciseDecimal string to standard notation by shifting the decimal point. */
function convertScientificPreciseDecimalStringToStandard(scientificValue: PreciseDecimal): PreciseDecimal {
  const {sign, scientificCoefficient, scientificExponent} =
    scientificValue.value.match(PreciseDecimal.PRECISE_DECIMAL_REGEX)?.groups ?? {};
  assert(
    scientificCoefficient != null && scientificExponent != null,
    'unable to parse from scientific notation',
    {value: scientificValue.value}
  );
  let decimalPosition = scientificCoefficient.indexOf('.');
  // If there is no decimal point, then it is an integer and the decimal position is the end of the string
  if (decimalPosition === -1) {
    decimalPosition = scientificCoefficient.length;
  }
  const coefficientString = scientificCoefficient.replace('.', '');
  const exponentInt: number = parseInt(scientificExponent, 10);
  const newDecimalPosition = decimalPosition + exponentInt;

  let unsignedDecimal: string;
  // Shift the decimal point to the left or right with additional zeros
  if (newDecimalPosition <= 0) {
    unsignedDecimal = `0.${'0'.repeat(-newDecimalPosition)}${coefficientString}`;
  } else if (newDecimalPosition >= coefficientString.length) {
    unsignedDecimal = `${coefficientString}${'0'.repeat(newDecimalPosition - coefficientString.length)}`;
  } else {
    unsignedDecimal = `${coefficientString.slice(0, newDecimalPosition)}.${coefficientString.slice(newDecimalPosition)}`;
  }
  // Return the standard decimal string
  return new PreciseDecimal(`${sign ?? ''}${unsignedDecimal}`);
}
